import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import type { Refund } from "@/interfaces/invoices";

const money = (v: string, ccy = "usd") =>
  `${ccy.toUpperCase()} ${Number(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const dateTime = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/** One label/value row in the detail sheet. Value wraps freely (the reason can be long). */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "baseline" }}>
      <Typography
        sx={{
          flex: "0 0 128px",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.4px",
          textTransform: "uppercase",
          color: "var(--color-dark-dust)",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, fontSize: 14, color: "var(--color-rodeo-black)", minWidth: 0 }}>
        {children}
      </Box>
    </Box>
  );
}

/**
 * Read-only detail sheet for one refund — opened from the eye icon on the Refunds blotter. Shows the
 * full reason (which is too long for a table cell) plus the rest of the record. No editing.
 */
export default function RefundDetailDialog({
  refund,
  onClose,
}: {
  refund: Refund | null;
  onClose: () => void;
}) {
  const isReversal = refund != null && refund.reversesRefundId != null;
  return (
    <Dialog open={refund != null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isReversal ? "Reversal details" : "Refund details"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.75, pt: 1 }}>
        {refund && (
          <>
            <Field label="Type">
              {isReversal ? (
                <Chip size="small" label="Reversal" variant="outlined" />
              ) : (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                  <Chip size="small" label="Refund" color="primary" variant="outlined" />
                  {refund.isReversed && (
                    <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                      reversed
                    </Typography>
                  )}
                </Box>
              )}
            </Field>
            <Field label="Amount">
              <strong>{money(refund.amount, refund.currency)}</strong>
            </Field>
            <Field label={isReversal ? "Reversal date" : "Refund date"}>
              {refund.refundDate.slice(0, 10)}
            </Field>
            <Field label="Invoice">{refund.invoiceNumber ?? `#${refund.invoiceId}`}</Field>
            <Field label="Organization">{refund.organizationName ?? "—"}</Field>
            <Field label="Group">{refund.groupName ?? "—"}</Field>
            <Field label="Reason">
              <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {refund.reason}
              </span>
            </Field>
            <Field label="Reference">{refund.reference ?? "—"}</Field>
            <Field label="Recorded by">{refund.actorName ?? "—"}</Field>
            <Field label="Recorded">{dateTime(refund.createdAt)}</Field>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
